import React, { useState, useEffect } from 'react';
import { Badge, Popover, List, Button, Space, Empty, Typography, Tag, message } from 'antd';
import { BellOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { NotificationService } from '@/services/notification.service';
import { TournamentInvitationService } from '@/services/tournamentInvitation.service';
import type { Notification } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
// Простая функция для форматирования времени без date-fns (упрощенная версия)
const formatDistanceToNow = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'} назад`;
  if (hours > 0) return `${hours} ${hours === 1 ? 'час' : hours < 5 ? 'часа' : 'часов'} назад`;
  if (minutes > 0) return `${minutes} ${minutes === 1 ? 'минуту' : minutes < 5 ? 'минуты' : 'минут'} назад`;
  return 'только что';
};

const { Text, Title } = Typography;

export const NotificationBell: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Загружаем уведомления только если пользователь авторизован
    if (isAuthenticated) {
      fetchNotifications();
      fetchUnreadCount();
      
      // Обновляем уведомления каждые 30 секунд
      const interval = setInterval(() => {
        fetchNotifications();
        fetchUnreadCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await NotificationService.getMyNotifications();
      // Сортируем: сначала непрочитанные, затем по дате (новые первые)
      const sorted = data.sort((a, b) => {
        if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setNotifications(sorted);
      setUnreadCount(sorted.filter(n => !n.isRead).length);
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const count = await NotificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      logger.error('Error fetching unread count:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    try {
      await NotificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      logger.error('Error marking notification as read:', error);
      message.error('Не удалось отметить уведомление как прочитанное');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      message.success('Все уведомления отмечены как прочитанные');
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
      message.error('Не удалось отметить все уведомления как прочитанные');
    }
  };

  const handleAcceptInvitation = async (invitationId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await TournamentInvitationService.acceptInvitation(invitationId);
      message.success('Приглашение принято');
      fetchNotifications();
      fetchUnreadCount();
    } catch (error: any) {
      logger.error('Error accepting invitation:', error);
      message.error(error?.response?.data?.message || 'Не удалось принять приглашение');
    }
  };

  const handleRejectInvitation = async (invitationId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await TournamentInvitationService.rejectInvitation(invitationId);
      message.success('Приглашение отклонено');
      fetchNotifications();
      fetchUnreadCount();
    } catch (error: any) {
      logger.error('Error rejecting invitation:', error);
      message.error(error?.response?.data?.message || 'Не удалось отклонить приглашение');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    
    // Уведомление о регистрации в клуб — переход в управление клубом
    if (notification.metadata?.type === 'clubRegistration' && notification.metadata?.clubId) {
      navigate('/club');
      setVisible(false);
      return;
    }
    
    // Определяем ID турнира из разных источников
    let tournamentId: number | undefined;
    
    // Для уведомлений о приглашениях на турнир
    if (notification.type === 'tournamentInvitation' && notification.tournamentInvitation) {
      tournamentId = notification.tournamentInvitation.tournamentId || notification.tournamentInvitation.tournament?.id;
    }
    
    // Также проверяем metadata, если там есть tournamentId
    if (!tournamentId && notification.metadata?.tournamentId) {
      tournamentId = notification.metadata.tournamentId;
    }
    
    // Переходим на страницу турнира, если ID найден
    if (tournamentId) {
      navigate(`/tournaments/${tournamentId}`);
      setVisible(false);
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString));
    } catch (error) {
      return dateString;
    }
  };

  const notificationContent = (
    <div style={{ width: 400, maxHeight: 500, overflowY: 'auto' }}>
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0 }}>Уведомления</Title>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={handleMarkAllAsRead}>
            Отметить все как прочитанные
          </Button>
        )}
      </div>
      
      {notifications.length === 0 ? (
        <Empty 
          description="Нет уведомлений" 
          style={{ padding: '40px 0' }}
        />
      ) : (
        <List
          dataSource={notifications}
          loading={loading}
          renderItem={(notification) => {
            const isInvitation = notification.type === 'tournamentInvitation';
            const invitation = notification.tournamentInvitation;
            
            return (
              <List.Item
                style={{ 
                  padding: '12px 16px',
                  cursor: 'pointer',
                  backgroundColor: notification.isRead ? 'transparent' : '#f0f8ff',
                  borderLeft: notification.isRead ? 'none' : '3px solid #1890ff'
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong={!notification.isRead}>{notification.title}</Text>
                      {!notification.isRead && (
                        <Badge dot style={{ backgroundColor: '#1890ff' }} />
                      )}
                    </Space>
                  }
                  description={
                    <div>
                      <div style={{ marginBottom: isInvitation ? 8 : 0 }}>
                        {notification.message}
                      </div>
                      {isInvitation && invitation && (
                        <div style={{ marginTop: 8 }}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Турнир: {invitation.tournament?.name || invitation.club?.name || 'Неизвестный турнир'}
                            </Text>
                            {invitation.status === 'pending' && (
                              <Space>
                                <Button 
                                  type="primary" 
                                  size="small" 
                                  icon={<CheckOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAcceptInvitation(invitation.id, e);
                                  }}
                                >
                                  Принять
                                </Button>
                                <Button 
                                  danger 
                                  size="small" 
                                  icon={<CloseOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRejectInvitation(invitation.id, e);
                                  }}
                                >
                                  Отклонить
                                </Button>
                              </Space>
                            )}
                            {invitation.status === 'accepted' && (
                              <Tag color="green">Принято</Tag>
                            )}
                            {invitation.status === 'rejected' && (
                              <Tag color="red">Отклонено</Tag>
                            )}
                          </Space>
                        </div>
                      )}
                      <Text type="secondary" style={{ fontSize: '11px', marginTop: 4, display: 'block' }}>
                        {formatTime(notification.createdAt)}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );

  // Показываем компонент только если пользователь авторизован
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Popover
      content={notificationContent}
      title={null}
      trigger="click"
      open={visible}
      onOpenChange={setVisible}
      placement="bottomRight"
      overlayStyle={{ paddingTop: 0 }}
    >
      <Badge count={unreadCount} size="small" offset={[-8, 8]}>
        <BellOutlined 
          style={{ 
            fontSize: 20, 
            cursor: 'pointer',
            color: unreadCount > 0 ? '#1890ff' : 'inherit'
          }} 
        />
      </Badge>
    </Popover>
  );
};

export default NotificationBell;